import { Link } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Calendar, Plus, ListFilter } from "lucide-react";
import { toast } from "sonner";
import { useTimezone } from "@/hooks/useTimezone";
import { ScheduleFilters } from "@/components/schedule/ScheduleFilters";
import { ScheduleItemCard } from "@/components/schedule/ScheduleItemCard";
import { groupScheduleItemsByDate, formatDateHeader } from "@/lib/utils";

interface ScheduleItem {
  id: string;
  title: string;
  description: string | null;
  start_time: string;
  end_time: string;
  timezone: string;
  event_type: string;
  status: string;
  recurrence: string | null;
  reminder_minutes: number;
}

const DashboardSchedule = () => {
  const { timezone, formatInTimezone, toUserTimezone } = useTimezone();
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_time: "",
    end_time: "",
    event_type: "task",
    recurrence: "none",
    reminder_minutes: 15,
  });

  useEffect(() => {
    loadSchedule();
  }, []);

  const loadSchedule = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("user_schedules")
        .select("*")
        .eq("user_id", user.id)
        .gte("end_time", new Date().toISOString())
        .order("start_time", { ascending: true });

      if (error) throw error;
      setScheduleItems(data || []);
    } catch (error) {
      console.error("Error loading schedule:", error);
      toast.error("Failed to load schedule");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    if (new Date(formData.end_time) <= new Date(formData.start_time)) {
      toast.error("End time must be after start time");
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      const startISO = new Date(formData.start_time).toISOString();
      const endISO = new Date(formData.end_time).toISOString();

      const payload = {
        ...formData,
        start_time: startISO,
        end_time: endISO,
        user_id: user.id,
        timezone,
        recurrence: formData.recurrence === "none" ? null : formData.recurrence,
      };

      if (editingId) {
        const { error } = await supabase
          .from("user_schedules")
          .update(payload)
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Schedule updated!");
      } else {
        const { error } = await supabase
          .from("user_schedules")
          .insert(payload);

        if (error) throw error;
        toast.success("Schedule item added!");
      }

      setShowDialog(false);
      resetForm();
      loadSchedule();
    } catch (error) {
      console.error("Error saving schedule:", error);
      toast.error("Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      start_time: "",
      end_time: "",
      event_type: "task",
      recurrence: "none",
      reminder_minutes: 15,
    });
    setEditingId(null);
  };

  const handleEdit = (item: ScheduleItem) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    const toLocalInput = (iso: string) => {
      const d = toUserTimezone(iso);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    setFormData({
      title: item.title,
      description: item.description || "",
      start_time: toLocalInput(item.start_time),
      end_time: toLocalInput(item.end_time),
      event_type: item.event_type,
      recurrence: item.recurrence || "none",
      reminder_minutes: item.reminder_minutes,
    });
    setEditingId(item.id);
    setShowDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this schedule item?")) return;

    try {
      const { error } = await supabase
        .from("user_schedules")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Schedule item deleted");
      loadSchedule();
    } catch (error) {
      console.error("Error deleting schedule:", error);
      toast.error("Failed to delete schedule item");
    }
  };

  const handleComplete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("user_schedules")
        .update({ status: "completed" })
        .eq("id", id);

      if (error) throw error;
      toast.success("Marked as completed!");
      loadSchedule();
    } catch (error) {
      console.error("Error completing schedule:", error);
      toast.error("Failed to update status");
    }
  };

  useEffect(() => {
    const timers: number[] = [];
    const now = new Date();

    scheduleItems.forEach((item) => {
      if (item.status === "completed") return;
      const start = new Date(item.start_time);
      const reminderMs = (item.reminder_minutes || 0) * 60_000;
      const fireAt = new Date(start.getTime() - reminderMs);
      const delay = fireAt.getTime() - now.getTime();

      if (delay > 0 && delay < 1000 * 60 * 60 * 24) {
        const id = window.setTimeout(() => {
          toast.info("Upcoming activity", {
            description: `${item.title} starts at ${formatInTimezone(item.start_time)}`,
          });
        }, delay);
        timers.push(id);
      }
    });

    return () => timers.forEach((id) => clearTimeout(id));
  }, [scheduleItems, formatInTimezone]);

  const filteredItems = useMemo(() => {
    if (!selectedType) return scheduleItems;
    return scheduleItems.filter((item) => item.event_type === selectedType);
  }, [scheduleItems, selectedType]);

  const groupedItems = useMemo(() => {
    return groupScheduleItemsByDate(filteredItems);
  }, [filteredItems]);

  const openDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Calendar className="w-8 h-8 text-primary" />
                Schedule
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage your time and stay on track
              </p>
            </div>
          </div>
          <Button onClick={openDialog} size="lg">
            <Plus className="w-5 h-5 mr-2" />
            Add Event
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <ListFilter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filter:</span>
              </div>
              <ScheduleFilters selectedType={selectedType} onTypeChange={setSelectedType} />
            </div>
          </CardContent>
        </Card>

        {/* Schedule List */}
        {loading ? (
          <Card>
            <CardContent className="p-12">
              <div className="flex flex-col items-center justify-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-sm text-muted-foreground">Loading schedule...</p>
              </div>
            </CardContent>
          </Card>
        ) : filteredItems.length === 0 ? (
          <Card>
            <CardContent className="p-12">
              <div className="flex flex-col items-center justify-center gap-4">
                <Calendar className="w-16 h-16 text-muted-foreground opacity-50" />
                <div className="text-center">
                  <p className="text-lg font-semibold mb-1">No schedule items</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedType ? "Try changing your filter" : "Add your first event to get started"}
                  </p>
                </div>
                {!selectedType && (
                  <Button onClick={openDialog} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Event
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Today */}
            {groupedItems.today.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <div className="h-8 w-1 bg-primary rounded-full" />
                  Today
                  <span className="text-sm text-muted-foreground font-normal ml-2">
                    {formatDateHeader(new Date())}
                  </span>
                </h2>
                <div className="space-y-3">
                  {groupedItems.today.map((item) => (
                    <ScheduleItemCard
                      key={item.id}
                      {...item}
                      startTime={item.start_time}
                      endTime={item.end_time}
                      eventType={item.event_type}
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      onComplete={() => handleComplete(item.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Tomorrow */}
            {groupedItems.tomorrow.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <div className="h-8 w-1 bg-accent rounded-full" />
                  Tomorrow
                  <span className="text-sm text-muted-foreground font-normal ml-2">
                    {formatDateHeader(new Date(Date.now() + 86400000))}
                  </span>
                </h2>
                <div className="space-y-3">
                  {groupedItems.tomorrow.map((item) => (
                    <ScheduleItemCard
                      key={item.id}
                      {...item}
                      startTime={item.start_time}
                      endTime={item.end_time}
                      eventType={item.event_type}
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      onComplete={() => handleComplete(item.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* This Week */}
            {groupedItems.thisWeek.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <div className="h-8 w-1 bg-secondary rounded-full" />
                  This Week
                </h2>
                <div className="space-y-3">
                  {groupedItems.thisWeek.map((item) => (
                    <ScheduleItemCard
                      key={item.id}
                      {...item}
                      startTime={item.start_time}
                      endTime={item.end_time}
                      eventType={item.event_type}
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      onComplete={() => handleComplete(item.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Later */}
            {groupedItems.later.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <div className="h-8 w-1 bg-muted rounded-full" />
                  Later
                </h2>
                <div className="space-y-3">
                  {groupedItems.later.map((item) => (
                    <ScheduleItemCard
                      key={item.id}
                      {...item}
                      startTime={item.start_time}
                      endTime={item.end_time}
                      eventType={item.event_type}
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      onComplete={() => handleComplete(item.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit" : "New"} Schedule Item</DialogTitle>
              <DialogDescription>
                {editingId ? "Update" : "Add"} an item to your schedule • Timezone: {timezone}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Course Module - Week 1"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Optional details..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start">Start Time *</Label>
                  <Input
                    id="start"
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="end">End Time *</Label>
                  <Input
                    id="end"
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="type">Type</Label>
                  <Select
                    value={formData.event_type}
                    onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="task">Task</SelectItem>
                      <SelectItem value="session">Session</SelectItem>
                      <SelectItem value="meeting">Meeting</SelectItem>
                      <SelectItem value="reminder">Reminder</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="recurrence">Recurrence</Label>
                  <Select
                    value={formData.recurrence}
                    onValueChange={(value) => setFormData({ ...formData, recurrence: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">One-time</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="reminder">Reminder (min)</Label>
                  <Input
                    id="reminder"
                    type="number"
                    min="0"
                    value={formData.reminder_minutes}
                    onChange={(e) => setFormData({ ...formData, reminder_minutes: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Add"} Schedule Item
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DashboardSchedule;
